import { format } from 'date-fns'

import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { InputFormValuesEdit } from '../types'
import { useSharedRoutesStore, useWorkstationStore } from '@/modules/Workstation/store'
import { useNotification } from '@/ui/Notification'
import { useCombinedLogsTableFormStateStore } from '../store'
import { ICreateControllerLogRequest, RouteSelectionMode, Log, IRtnTripAsString } from '@/types/logs'
import { useAuthStore } from '@/store/auth-store'
import { useGarageStore } from '@/modules/FindGarage'
import { Role } from '@/types/auth'
import { formatCreateLogDate } from '../helpers/formatCreateLogDate'
import { useDateStore } from '@/components/DateInput'
import { isValidTimeFormat } from '@/helpers/isValidTimeFormat'
import { useGetCodes } from './useGetCodes'
import { formatLogRequestBody } from '../helpers/formatLogRequestBody'
import { getCodeTypeId } from '../helpers/getCodeTypeId'
import { useMemo } from 'react'
import { formatInTimeZone } from 'date-fns-tz'

interface ICreateControllerLogResponse {
    id: string
}

export const useUpdateSharedLogs = () => {
    const queryClient = useQueryClient()
    const { routes, workstation } = useWorkstationStore()
    const { garage } = useGarageStore()
    const { showNotification } = useNotification()
    const { selectedTrips, selectedOriginalTrips, report, mechanicalInfo, locationType, isBulkCurtailment } =
        useCombinedLogsTableFormStateStore()
    const { role } = useAuthStore()
    const { date } = useDateStore()
    const { data: codes = [] } = useGetCodes()
    const { sharedRoutes } = useSharedRoutesStore()

    const params = useMemo(() => {
        const formattedDate = formatInTimeZone(date, 'UTC', "yyyy-MM-dd'T'HH:mm:ss")

        return [formattedDate, sharedRoutes.map((route) => route.route)]
    }, [date, sharedRoutes])

    const clParams = useMemo(() => {
        return ['controllerLogs', ...params]
    }, [params])

    const updateControllerLogMutation = useAppMutation<
        ICreateControllerLogResponse,
        Error,
        ICreateControllerLogRequest & { selectedRowId: string },
        Log[]
    >({
        mutationFn: ({ selectedRowId, ...data }) =>
            createInstance({
                url: `/controller-logs/${selectedRowId}`,
                method: 'PUT',
                data,
            }),
        alertOnError: true,
        queryKey: clParams,
        optimisticChangesCallback: (requestBody: ICreateControllerLogRequest, previousLogs?: Log[]) => {
            const updatedLog = formatLogRequestBody({ requestBody, codes })
            return previousLogs?.map((item) =>
                item.id === updatedLog.id ? { ...updatedLog, originalAuthor: item.originalAuthor } : item
            )
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['controllerLogs'] })
            showNotification({ message: 'Record updated' })
        },
    })

    const handleUpdateSharedLogs = (data: InputFormValuesEdit, selectedRowId: string, previousTimeStamp?: string) => {
        if (!selectedRowId) throw new Error('No selected row id')

        const selectedRoutes = role === Role.MileageClerk ? garage?.routes : routes.map((route) => route.route)

        const selectedTimeStamp = isValidTimeFormat(data.timeStamp)
            ? data.timeStamp
            : previousTimeStamp && isValidTimeFormat(previousTimeStamp)
              ? previousTimeStamp
              : format(new Date(), 'HH:mm')

        const timeStamp = formatCreateLogDate(date, selectedTimeStamp)

        const performUpdate = () => {
            updateControllerLogMutation.mutate({
                selectedRowId,
                id: selectedRowId,
                code: data.code ? data.code : undefined,
                codeTypeId: getCodeTypeId(data.code),
                reference: data.reference,
                routes: data.routes && data.routes !== 'N/A' ? data.routes.split(', ') : undefined,
                routeSelectionMode: data.routes
                    ? data.routes === 'N/A'
                        ? RouteSelectionMode.All
                        : RouteSelectionMode.Default
                    : RouteSelectionMode.None,
                trip: data.trip,
                rtnTrips:
                    !!selectedTrips.length && !isBulkCurtailment
                        ? selectedTrips.map(
                              (trip) => ({ trip: String(trip.trip), order: trip.order }) as IRtnTripAsString
                          )
                        : data.rtnTrips
                          ? (String(data.rtnTrips)
                                .split(',')
                                .map((trip) => ({ trip: trip, order: 0 })) as IRtnTripAsString[])
                          : undefined,
                run: data.run ? data.run : undefined,
                garageCode: data.depotCode ? data.depotCode : undefined,
                duty: data.duty ? data.duty.split('/') : undefined,
                fleet: data.fleet ? data.fleet.split('/') : undefined,
                locationFrom: data.locationFrom,
                locationTo: data.locationTo,
                locationType: locationType ?? undefined,
                description: data.description ? data.description : undefined,
                extraInfo: data.extraInfo ? data.extraInfo : undefined,
                strategy: data.strategy ? data.strategy : undefined,
                delay: data.delay ? data.delay : undefined,
                mileage: data.mileage ? +data.mileage : undefined,
                isZeroMileage: data.isZeroMileage === 'true',
                author: data.author,
                timeStamp,
                workstation: workstation?.workstationNumber,
                report,
                mechanicalInfo,
                originalTrip:
                    data.code === 'E'
                        ? {
                              routes: data.originalRoutes
                                  ? data.originalRoutes === 'N/A'
                                      ? selectedRoutes
                                      : [data.originalRoutes]
                                  : undefined,
                              trip: data.originalTrip,
                              rtnTrips:
                                  selectedOriginalTrips.length > 0
                                      ? selectedOriginalTrips.map(
                                            (trip) =>
                                                ({
                                                    trip: String(trip.trip),
                                                    order: trip.order,
                                                }) as IRtnTripAsString
                                        )
                                      : data.originalRtnTrips
                                        ? data.originalRtnTrips
                                              .split(',')
                                              .map((trip) => ({ trip: trip, order: 0 }) as IRtnTripAsString)
                                        : undefined,
                              run: data.originalRun ? data.originalRun : undefined,
                              duty: data.originalDuty ? data.originalDuty.split('/') : undefined,
                              fleet: data.originalFleet ? data.originalFleet.split('/') : undefined,
                          }
                        : undefined,
                eventLogId: data.eventLogId,
                selectedCodeTypeReasonId: data.selectedCodeTypeReasonId || '',
                direction: data.direction,
                bulkCurtailment:
                    data.bulkCurtailmentTripTo || isBulkCurtailment
                        ? {
                              tripTo: data.bulkCurtailmentTripTo?.toString() || '',
                              runTo: data.bulkCurtailmentRunTo?.toString() || '',
                              dutyTo: data.bulkCurtailmentDutyTo?.toString() || '',
                              fleetTo: data.bulkCurtailmentFleetTo?.toString() || '',
                          }
                        : undefined,
            })
        }

        if (!isValidTimeFormat(data.timeStamp)) {
            showNotification({
                type: 'error',
                title: 'Time cannot be blank',
                message: `Your updates to ${data.reference} will be saved with the time ${selectedTimeStamp} on ${format(timeStamp, 'dd MMM yyyy')} because the time field cannot be left blank.`,
                manualDismiss: true,
                errorDismissTitle: 'Close',
                onDismiss: performUpdate,
            })
        } else {
            performUpdate()
        }
    }

    return { handleUpdateSharedLogs, isPending: updateControllerLogMutation.isPending }
}
